import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { UserDataShared } from '../models/interface/userData.interface';

interface FreshchatWidget {
  init(config: object): void;
  destroy(): void;
  open(): void;
  isInitialized(): boolean;
  isLoaded(): boolean;
  on(event: string, handler: () => void): void;
  user: { setProperties(properties: object): void; clear(): Promise<unknown> };
}

/** Legacy Freshchat widget, kept as the control variant of the chat experiment. */
@Injectable({
  providedIn: 'root',
})
export class FreshchatService {
  private loading: Promise<boolean> | null = null;

  constructor(@Inject(DOCUMENT) private document: Document) {}

  /** Loads the widget library once; a failed load can be retried. */
  load(): Promise<boolean> {
    this.loading ??= new Promise<boolean>(resolve => {
      const script = this.document.createElement('script');
      script.addEventListener('load', () => resolve(true));
      script.addEventListener('error', () => {
        this.loading = null;
        resolve(false);
      });
      script.src = `${environment.freshChat.host}/js/widget.js`;
      script.type = 'text/javascript';
      script.async = true;
      script.defer = true;
      this.document.head.appendChild(script);
    });
    return this.loading;
  }

  init(userData: UserDataShared | null): void {
    const widget = this.widget();
    if (!widget) {
      return environment.guardClause;
    }
    if (!userData?.id) {
      if (widget.isInitialized()) {
        widget.user.clear().catch(() => undefined);
        return environment.guardClause;
      }
      widget.init(environment.freshChat);
      return environment.guardClause;
    }
    const userParams = {
      externalId: userData.id,
      firstName: userData.name,
      email: userData.email,
      phone: userData.userPhone,
      companyId: userData.companyId,
    };
    if (widget.isInitialized()) {
      widget.user.setProperties(userParams);
      return environment.guardClause;
    }
    widget.init({ ...environment.freshChat, ...userParams });
  }

  open(): void {
    const widget = this.widget();
    if (!widget) {
      return environment.guardClause;
    }
    if (widget.isLoaded()) {
      widget.open();
      return environment.guardClause;
    }
    widget.on('widget:loaded', () => widget.open());
  }

  destroy(): void {
    this.widget()?.destroy();
  }

  private widget(): FreshchatWidget | undefined {
    return (window as unknown as { fcWidget?: FreshchatWidget }).fcWidget;
  }
}
